import { useQueryClient } from '@tanstack/react-query';
import { useDependency } from 'ditox-react';
import { useTranslation } from 'react-i18next';
import { useBoolean } from 'usehooks-ts';
import { WEB_SOCKET_MANAGER_TOKEN } from '@/container';
import useFriends from '@/hooks/contacts/use-friends';
import { allChatSessionQueryOptions } from '@/hooks/sessions/use-conversation-sessions';
import { ConversationMessageContentType } from '@/models/conversations/conversation';
import type { ConversationType } from '@/models/conversations/conversation-session';
import { SendMessageCommand } from '@/models/messages/send-message';
import ROUTES from '@/routes';
import { getUserId } from '@/stores/auth';
import { clearLastPosition } from '@/stores/conversation-session';
import { addToast } from '@/stores/toast';

export default function useSendContact(type: ConversationType, id: Nullable<number>) {
  const { t } = useTranslation();
  const { value: isLoading, setTrue: setLoading, setFalse: setNotLoading } = useBoolean(false);
  const queryClient = useQueryClient();
  const { friends } = useFriends();
  const webSocketManager = useDependency(WEB_SOCKET_MANAGER_TOKEN);

  const sendContact = (friendId: Nullable<number>): boolean => {
    if (!friendId || !id || isLoading) return false;

    const friend = friends[friendId];
    if (!friend) return false;

    setLoading();
    const Message = JSON.stringify({
      HeadImg: friend.FriendHeadImg,
      UserID: friend.FriendID,
      NickName: friend.FriendNickName,
      VChatID: friend.VChatID,
    });

    const userId = getUserId();
    if (!userId) return false;

    webSocketManager.dispatch({
      action: 'send',
      params: {
        message:
          type === 'session'
            ? {
                Cmd: SendMessageCommand.Chat,
                UserID: userId,
                Message,
                ChatSessionID: id,
                MsgType: ConversationMessageContentType.Contact,
                TimeStamp: Date.now(),
              }
            : {
                Cmd: SendMessageCommand.GroupChat,
                Message,
                UserID: userId,
                GroupID: id,
                MsgType: ConversationMessageContentType.Contact,
                TimeStamp: Date.now(),
                AtIDs: null,
              },
      },
    });

    queryClient.refetchQueries(allChatSessionQueryOptions).catch(console.error);
    clearLastPosition(
      type === 'group'
        ? ROUTES.MESSAGES.GROUPS.$buildPath({ params: { id } })
        : ROUTES.MESSAGES.SESSIONS.$buildPath({ params: { id } }),
    );
    setNotLoading();

    addToast({
      type: 'success',
      message: t('common.done'),
    });

    return true;
  };

  return {
    sendContact,
    isLoading,
  };
}
